import { useCallback, useEffect, useRef, useState } from 'react';
import {
  STORAGE_KEY,
  getLockState,
  normalizeEmail,
  registerFailure as registerFailureFn,
  clearFailures as clearFailuresFn,
} from './loginThrottle';

// Hook sobre loginThrottle: mantem o estado do bloqueio, roda a contagem
// regressiva de 1s enquanto bloqueado e sincroniza entre abas (evento storage).
const useLoginThrottle = (email) => {
  const key = normalizeEmail(email);
  const [state, setState] = useState(() => getLockState(key));
  const intervalRef = useRef(null);

  const refresh = useCallback(() => {
    setState(getLockState(key));
  }, [key]);

  // Recomputa quando o email digitado muda.
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Contagem regressiva enquanto bloqueado; some sozinha ao zerar.
  useEffect(() => {
    if (!state.locked) return undefined;

    intervalRef.current = setInterval(() => {
      const next = getLockState(key);
      setState(next);
      if (!next.locked && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [state.locked, key]);

  // Outra aba mexeu no throttle.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key === STORAGE_KEY) refresh();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refresh]);

  const registerFailure = useCallback((options) => {
    const next = registerFailureFn(key, options);
    setState(next);
    return next;
  }, [key]);

  const clearFailures = useCallback(() => {
    clearFailuresFn(key);
    setState(getLockState(key));
  }, [key]);

  return {
    locked: state.locked,
    remainingMs: state.remainingMs,
    remainingLabel: state.remainingLabel,
    failureCount: state.failureCount,
    registerFailure,
    clearFailures,
  };
};

export default useLoginThrottle;
